// Deferred section loading.
// Sections opting in render their real markup inside a <template> tag inside
// a <deferred-section> wrapper. Browsers parse but do NOT execute <template>
// content (no image/script/iframe loads), so the section is effectively inert
// until we activate it.
//
// Activation rules:
//  - In the theme editor: activate immediately so merchants can edit.
//  - Otherwise: activate when the section scrolls into view (or near it),
//    via IntersectionObserver with a generous rootMargin so content is ready
//    just before the user reaches it. Each section is independent — scrolling
//    past one does not activate sections still far below.
//  - Hash navigation: if the target id is still inside a <template>, activate
//    that section then scroll. Yield before the clone so the click can paint.

const supportsIO = typeof IntersectionObserver !== 'undefined';

const io = supportsIO
  ? new IntersectionObserver(
      (entries, observer) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            observer.unobserve(entry.target);
            entry.target.activate();
          }
        }
      },
      { rootMargin: '800px 0px', threshold: 0 }
    )
  : null;

class DeferredSection extends HTMLElement {
  connectedCallback() {
    if (window.Shopify && window.Shopify.designMode) {
      this.activate();
      return;
    }

    if (!supportsIO) {
      // Fallback: activate immediately on browsers without IntersectionObserver.
      this.activate();
      return;
    }

    io.observe(this);
  }

  disconnectedCallback() {
    if (io) io.unobserve(this);
  }

  activate() {
    const tpl = this.querySelector(':scope > template');
    if (!tpl) {
      console.warn('deferred-section: no <template> child found');
      return;
    }
    const frag = tpl.content.cloneNode(true);
    // Force lazy loading on every image inside the activated section so the
    // browser only fetches them when they actually intersect the viewport.
    frag.querySelectorAll('img').forEach((img) => {
      if (!img.hasAttribute('loading')) img.setAttribute('loading', 'lazy');
      if (!img.hasAttribute('decoding')) img.setAttribute('decoding', 'async');
    });
    frag.querySelectorAll('iframe').forEach((iframe) => {
      if (!iframe.hasAttribute('loading')) iframe.setAttribute('loading', 'lazy');
    });
    // <script> elements cloned from a <template> are inert — replace each one
    // with a freshly created script so it executes on insertion.
    frag.querySelectorAll('script').forEach((oldScript) => {
      const newScript = document.createElement('script');
      for (const attr of oldScript.attributes) {
        newScript.setAttribute(attr.name, attr.value);
      }
      newScript.textContent = oldScript.textContent;
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
    const parent = this.parentNode;
    if (!parent) return;
    parent.insertBefore(frag, this);
    parent.removeChild(this);
    document.dispatchEvent(new CustomEvent('deferred-section:activated', { bubbles: true }));
  }
}

customElements.define('deferred-section', DeferredSection);

(() => {
  function findDeferredHost(id) {
    const hosts = document.querySelectorAll('deferred-section');
    for (let i = 0; i < hosts.length; i++) {
      const tpl = hosts[i].querySelector(':scope > template');
      if (tpl && tpl.content.querySelector('#' + id)) return hosts[i];
    }
    return null;
  }

  async function revealHashId(id) {
    if (!id) return;
    if (!document.getElementById(id)) {
      const host = findDeferredHost(id);
      if (!host) return;
      await new Promise((resolve) => setTimeout(resolve, 0));
      if (!document.getElementById(id) && host.isConnected) host.activate();
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView();
  }

  document.addEventListener(
    'click',
    (event) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = event.target.closest && event.target.closest('a[href]');
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href || href.charAt(0) !== '#') return;
      const id = href.slice(1);
      if (!id || document.getElementById(id) || !findDeferredHost(id)) return;
      event.preventDefault();
      revealHashId(id);
      if (location.hash !== '#' + id) location.hash = id;
    },
    true
  );

  window.addEventListener('hashchange', () => {
    if (location.hash.length > 1) revealHashId(location.hash.slice(1));
  });

  if (location.hash.length > 1) revealHashId(location.hash.slice(1));
})();
